import { Building2, Check, Image as ImageIcon, Info, MessageSquare, Upload } from "lucide-react";
import { MessageTemplatesPanel } from "../components/MessageTemplatesPanel";
import { useEffect, useState } from "react";
import { Shell } from "../components/Shell";
import { company as companyApi, type CompanyProfile, type CompanySettings, type CompanyUpdate } from "../lib/api";
import { useUser } from "../lib/auth";
import { StaffManager } from "../components/StaffManager";
import { ChangePassword } from "../components/ChangePassword";
import { AttorneyBrianConnect } from "../components/AttorneyBrianConnect";

const TIER_LABEL: Record<CompanyProfile["tier"], string> = {
  owner: "Platform owner",
  mentorship: "Mentorship",
  developer: "Developer package",
};

export default function SettingsPage() {
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [form, setForm] = useState<CompanySettings | null>(null);
  // Write-only secret inputs (never populated from the server).
  const [ghlKey, setGhlKey] = useState("");
  const [twSid, setTwSid] = useState("");
  const [twToken, setTwToken] = useState("");
  const [lobKey, setLobKey] = useState("");
  const [lsId, setLsId] = useState("");
  const [lsKey, setLsKey] = useState("");
  const [discordHook, setDiscordHook] = useState("");
  const [discordClear, setDiscordClear] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const { user } = useUser();

  async function uploadLogo(file: File) {
    setLogoError(null);
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      setLogoError("Logo must be a PNG, JPG, or WebP image."); return;
    }
    if (file.size > 1_048_576) {
      setLogoError(`That file is ${(file.size / 1024 / 1024).toFixed(2)} MB — the limit is 1 MB.`); return;
    }
    setLogoUploading(true);
    try {
      const r = await companyApi.uploadLogo(file);
      set("logo_url", r.logo_url);
    } catch (e: any) {
      setLogoError(e?.message || "Upload failed");
    } finally {
      setLogoUploading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    companyApi.get()
      .then((r) => { if (!cancelled) { setProfile(r.company); setForm({ ...r.settings, intake_link: r.intake_link ?? null }); } })
      .catch((e) => { if (!cancelled) setError(e?.message || "Could not load company settings"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  function set<K extends keyof CompanySettings>(key: K, value: CompanySettings[K]) {
    setForm((f) => (f ? { ...f, [key]: value } : f));
    setSaved(false);
  }

  async function save() {
    if (!form) return;
    setSaving(true); setError(null); setSaved(false);
    const patch: CompanyUpdate = {
      business_name: form.business_name, legal_entity: form.legal_entity, logo_url: form.logo_url,
      from_email: form.from_email, reply_to_email: form.reply_to_email,
      internal_notification_email: form.internal_notification_email, support_email: form.support_email,
      phone: form.phone, address_line1: form.address_line1, address_city: form.address_city,
      address_state: form.address_state, address_zip: form.address_zip,
      mfsn_affiliate_url: form.mfsn_affiliate_url, smartcredit_affiliate_url: form.smartcredit_affiliate_url,
      comms_provider: form.comms_provider, ghl_location_id: form.ghl_location_id, twilio_from: form.twilio_from,
      // Out-of-range numbers go to the server so it can reject them visibly.
      funding_min_score: typeof form.funding_min_score === "number" ? form.funding_min_score : undefined,
      import_failure_alerts: !!form.import_failure_alerts,
    };
    if (form.intake_link) {
      patch.intake_link_enabled = form.intake_link.enabled;
      if (form.intake_link.default_record_type) patch.intake_default_record_type = form.intake_link.default_record_type;
    }
    if (ghlKey.trim()) patch.ghl_api_key = ghlKey.trim();
    if (discordHook.trim()) patch.discord_webhook_url = discordHook.trim();
    else if (discordClear) patch.discord_webhook_url = "";
    if (twSid.trim()) patch.twilio_account_sid = twSid.trim();
    if (twToken.trim()) patch.twilio_auth_token = twToken.trim();
    if (lobKey.trim()) patch.lob_api_key = lobKey.trim();
    if (lsId.trim()) patch.letterstream_api_id = lsId.trim();
    if (lsKey.trim()) patch.letterstream_api_key = lsKey.trim();
    try {
      await companyApi.update(patch);
      setSaved(true);
      setGhlKey(""); setTwSid(""); setTwToken("");
      setLobKey(""); setLsId(""); setLsKey("");
      const r = await companyApi.get();
      setProfile(r.company); setForm({ ...r.settings, intake_link: r.intake_link ?? null });
    } catch (e: any) {
      setError(e?.message || "Could not save settings");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Shell title="Company Settings" subtitle="Your business profile">
      {loading || !form ? (
        <div className="panel"><p className="muted">Loading…</p></div>
      ) : (
        <>
          {/* Company header */}
          <div className="panel" style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 14 }}>
            <div className="stat-icon"><Building2 size={20} aria-hidden="true" /></div>
            <div style={{ flex: 1 }}>
              <strong style={{ fontSize: 16 }}>{profile?.name}</strong>
              <div className="muted small">
                {profile ? TIER_LABEL[profile.tier] : ""}
                {profile?.subscription_status && profile.subscription_status !== "none" ? ` · subscription ${profile.subscription_status}` : ""}
              </div>
            </div>
          </div>

          {/* Branding-boundary note for mentorship companies */}
          {profile && !profile.can_whitelabel_software && (
            <div className="banner-warning banner-rail" style={{ marginBottom: 16, display: "flex", gap: 10, alignItems: "flex-start" }}>
              <Info size={16} aria-hidden="true" style={{ marginTop: 2, flexShrink: 0 }} />
              <span>
                These settings control what <strong>your clients</strong> see — your business name, logo, and the emails/texts sent on your behalf.
                The software itself stays <strong>Alief Dispute Hub</strong>. To put your own brand on the app and use your own domain, ask about the developer package.
              </span>
            </div>
          )}

          {error && <div className="banner-warning" style={{ marginBottom: 16 }}>{error}</div>}

          {/* Client-facing branding */}
          <div className="panel" style={{ marginBottom: 16 }}>
            <div className="panel-header"><h2>Client-facing branding</h2></div>
            <div className="stacked-form">
              <Field label="Business name (shown to your clients)">
                <input value={form.business_name ?? ""} onChange={(e) => set("business_name", e.target.value)} />
              </Field>
              <div className="form-field">
                <span>Logo</span>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <div style={{ width: 64, height: 64, border: "1px solid var(--ne-silver-1)", background: "var(--ne-silver-3)", display: "grid", placeItems: "center", flexShrink: 0, overflow: "hidden" }}>
                    {form.logo_url
                      ? <img src={form.logo_url} alt="Company logo" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                      : <ImageIcon size={22} aria-hidden="true" style={{ color: "var(--ne-silver-2)" }} />}
                  </div>
                  <div>
                    <label className="btn-secondary btn-sm" style={{ cursor: logoUploading ? "wait" : "pointer", display: "inline-flex" }}>
                      <Upload size={13} aria-hidden="true" /> {logoUploading ? "Uploading…" : "Upload logo"}
                      <input type="file" accept="image/png,image/jpeg,image/webp" style={{ display: "none" }} disabled={logoUploading}
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(f); e.target.value = ""; }} />
                    </label>
                    <p className="muted small" style={{ margin: "6px 0 0" }}>PNG, JPG, or WebP · up to 1 MB · transparent PNG under 500 KB is ideal.</p>
                    {logoError && <p className="error-text" style={{ margin: "4px 0 0" }}>{logoError}</p>}
                  </div>
                </div>
              </div>
              <Field label="Legal entity (optional)"><input value={form.legal_entity ?? ""} onChange={(e) => set("legal_entity", e.target.value)} /></Field>
              <div className="form-row-2">
                <Field label="From email (outbound to clients)"><input type="email" value={form.from_email ?? ""} onChange={(e) => set("from_email", e.target.value)} placeholder="you@yourbusiness.com" /></Field>
                <Field label="Reply-to email"><input type="email" value={form.reply_to_email ?? ""} onChange={(e) => set("reply_to_email", e.target.value)} /></Field>
              </div>
              <div className="form-row-2">
                <Field label="Support email"><input type="email" value={form.support_email ?? ""} onChange={(e) => set("support_email", e.target.value)} /></Field>
                <Field label="Phone"><input value={form.phone ?? ""} onChange={(e) => set("phone", e.target.value)} /></Field>
              </div>
              <div className="form-row">
                <Field label="Address"><input value={form.address_line1 ?? ""} onChange={(e) => set("address_line1", e.target.value)} /></Field>
                <Field label="City"><input value={form.address_city ?? ""} onChange={(e) => set("address_city", e.target.value)} /></Field>
                <Field label="State"><input value={form.address_state ?? ""} onChange={(e) => set("address_state", e.target.value)} /></Field>
              </div>
            </div>
          </div>

          {/* Internal notifications */}
          <div className="panel" style={{ marginBottom: 16 }}>
            <div className="panel-header"><h2>Internal notifications</h2></div>
            <div className="stacked-form">
              <Field label="Send my internal alerts to (e.g. when a client signs)">
                <input type="email" value={form.internal_notification_email ?? ""} onChange={(e) => set("internal_notification_email", e.target.value)} placeholder="you@yourbusiness.com" />
              </Field>
              <Field label={`Discord channel webhook ${form.discord_configured ? "(saved — leave blank to keep)" : "(optional)"}`}>
                <input type="url" inputMode="url" value={discordHook} onChange={(e) => { setDiscordHook(e.target.value); setDiscordClear(false); setSaved(false); }}
                  placeholder={form.discord_configured ? "•••••••• saved" : "https://discord.com/api/webhooks/…"} autoComplete="off" />
              </Field>
              <p className="muted small" style={{ margin: 0 }}>
                Posts to your own Discord channel when someone signs up through your link, finishes their intake, or pays an invoice.
                In Discord: open the channel → Edit Channel → Integrations → Webhooks → New Webhook → Copy Webhook URL.
                {form.discord_configured && (
                  <> {" "}<button type="button" className="btn-link small" onClick={() => { setDiscordClear(true); setDiscordHook(""); setSaved(false); }}>{discordClear ? "Will be removed on save" : "Remove"}</button></>
                )}
              </p>
            </div>
          </div>

          {/* Credit-monitoring affiliate enrollment links */}
          <div className="panel" style={{ marginBottom: 16 }}>
            <div className="panel-header"><h2>Credit monitoring — client enrollment</h2></div>
            <p className="muted small" style={{ margin: "0 0 12px" }}>
              Paste your affiliate enrollment links. Clients who don't already have monitoring
              see a "Sign up" button on their intake — enrolling through your link makes you the
              enroller so you can pull their 3-bureau report.
            </p>
            <div className="stacked-form">
              <Field label="MyFreeScoreNow affiliate link">
                <input type="url" inputMode="url" value={form.mfsn_affiliate_url ?? ""}
                  onChange={(e) => set("mfsn_affiliate_url", e.target.value)}
                  placeholder="https://www.myfreescorenow.com/..." autoComplete="off" />
              </Field>
              <Field label="SmartCredit affiliate link (optional)">
                <input type="url" inputMode="url" value={form.smartcredit_affiliate_url ?? ""}
                  onChange={(e) => set("smartcredit_affiliate_url", e.target.value)}
                  placeholder="https://www.smartcredit.com/..." autoComplete="off" />
              </Field>
            </div>
          </div>

          <div className="panel">
            <h2 style={{ marginTop: 0 }}>Funding Hub</h2>
            <p className="muted small" style={{ margin: "0 0 12px" }}>
              Clients whose middle credit score is at or above this number show a
              "Send to Funding Hub" button and appear in the Funding Ready list.
            </p>
            <div className="stacked-form">
              <Field label="Funding Ready minimum middle score">
                <input type="number" inputMode="numeric" min={300} max={850} step={1}
                  value={form.funding_min_score ?? 680}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    set("funding_min_score", Number.isFinite(n) ? n : null);
                  }} />
              </Field>
            </div>
          </div>

          {/* General sign-up link — docs/INTAKE-LINK-SPEC.md */}
          {form.intake_link && (
            <SignupLinkCard
              link={form.intake_link}
              onChange={(next) => set("intake_link", next)}
            />
          )}

          {/* Comms provider */}
          <div className="panel" style={{ marginBottom: 16 }}>
            <div className="panel-header"><h2>Mailing accounts — your own carriers</h2></div>
            <div className="stacked-form">
              <p className="muted small" style={{ margin: 0 }}>
                Dispute letters mail through <strong>your</strong> Lob and/or LetterStream account —
                your API keys, your postage billing. Add at least one before sending physical mail.
                You pick the carrier on every mailing; there is no default.
              </p>
              <Field label={`Lob API key ${form.lob_configured ? "(saved — leave blank to keep)" : ""}`}>
                <input type="password" value={lobKey} onChange={(e) => { setLobKey(e.target.value); setSaved(false); }} placeholder={form.lob_configured ? "•••••••• saved" : "live_… from dashboard.lob.com → Settings → API keys"} autoComplete="off" />
              </Field>
              <Field label={`LetterStream API ID ${form.letterstream_configured ? "(saved — leave blank to keep)" : ""}`}>
                <input type="password" value={lsId} onChange={(e) => { setLsId(e.target.value); setSaved(false); }} placeholder={form.letterstream_configured ? "•••••••• saved" : "From LetterStream support (API access requires their approval)"} autoComplete="off" />
              </Field>
              <Field label="LetterStream API key">
                <input type="password" value={lsKey} onChange={(e) => { setLsKey(e.target.value); setSaved(false); }} placeholder={form.letterstream_configured ? "•••••••• saved" : "LetterStream API key"} autoComplete="off" />
              </Field>
            </div>
          </div>

          <div className="panel" style={{ marginBottom: 16 }}>
            <div className="panel-header"><h2>Texting &amp; email provider</h2></div>
            <div className="stacked-form">
              <div className="channel-picker" role="radiogroup" aria-label="Comms provider">
                {(["log", "ghl", "twilio"] as const).map((p) => (
                  <button
                    key={p}
                    type="button"
                    role="radio"
                    aria-checked={form.comms_provider === p}
                    className={form.comms_provider === p ? "active" : ""}
                    onClick={() => set("comms_provider", p)}
                  >
                    <MessageSquare size={14} aria-hidden="true" />
                    {p === "log" ? "None (log only)" : p === "ghl" ? "GoHighLevel" : "Twilio"}
                  </button>
                ))}
              </div>

              {form.comms_provider === "ghl" && (
                <>
                  <Field label={`GHL API key ${form.ghl_configured ? "(saved — leave blank to keep)" : ""}`}>
                    <input type="password" value={ghlKey} onChange={(e) => { setGhlKey(e.target.value); setSaved(false); }} placeholder={form.ghl_configured ? "•••••••• saved" : "Paste GHL API key"} autoComplete="off" />
                  </Field>
                  <Field label="GHL Location ID"><input value={form.ghl_location_id ?? ""} onChange={(e) => set("ghl_location_id", e.target.value)} /></Field>
                  <p className="muted small" style={{ margin: 0 }}>
                    Use a GoHighLevel <strong>Private Integration</strong> token with these scopes: View/Edit Contacts and View/Edit Conversations
                    (plus Conversation Messages). Emails and texts you send from a client's Communication tab go out through GHL, so replies show up there too.
                  </p>
                </>
              )}

              <label style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 8 }}>
                <input
                  type="checkbox"
                  checked={!!form.import_failure_alerts}
                  onChange={(e) => set("import_failure_alerts", e.target.checked as any)}
                  style={{ marginTop: 3 }}
                />
                <span>
                  <strong>Tell clients automatically when their report pull fails</strong>
                  <span className="muted small" style={{ display: "block" }}>
                    When an automatic pull fails because the client's MyFreeScoreNow lapsed, MyFreeScoreNow wants its emailed code,
                    or their password was not accepted, the client gets an email, a portal message and (with GoHighLevel connected) a text
                    with the exact fix. At most once a week per problem. Edit the wording under Communication templates.
                  </span>
                </span>
              </label>

              {form.comms_provider === "twilio" && (
                <>
                  <Field label={`Twilio Account SID ${form.twilio_configured ? "(saved — leave blank to keep)" : ""}`}>
                    <input type="password" value={twSid} onChange={(e) => { setTwSid(e.target.value); setSaved(false); }} placeholder={form.twilio_configured ? "•••••••• saved" : "ACxxxxxxxx"} autoComplete="off" />
                  </Field>
                  <Field label="Twilio Auth Token">
                    <input type="password" value={twToken} onChange={(e) => { setTwToken(e.target.value); setSaved(false); }} placeholder={form.twilio_configured ? "•••••••• saved" : "Auth token"} autoComplete="off" />
                  </Field>
                  <Field label="Twilio from number"><input value={form.twilio_from ?? ""} onChange={(e) => set("twilio_from", e.target.value)} placeholder="+15555550123" /></Field>
                </>
              )}
            </div>
          </div>

          <div className="form-actions" style={{ alignItems: "center" }}>
            {saved && <span className="muted small" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--ne-success)" }}><Check size={15} aria-hidden="true" /> Saved</span>}
            <button className="btn-primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save settings"}</button>
          </div>

          {/* Attorney Brian MCP connect — visible to all staff, manageable by owner/manager. */}
          <MessageTemplatesPanel canEdit={user?.role === "owner" || user?.role === "manager"} />

          <AttorneyBrianConnect canManage={user?.role === "owner"} />

          {/* Manage staff / admin — owner-only. */}
          {user?.role === "owner" && <StaffManager currentUserId={user.id} />}

          {/* Every staff member can change their own password. */}
          <ChangePassword />
        </>
      )}
    </Shell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="form-field">
      <span>{label}</span>
      {children}
    </label>
  );
}

/**
 * "Your sign-up link" — the one URL a company texts to a new prospect, puts on
 * its website, or embeds in a funnel. Prospects who submit become a lead or
 * client automatically and land in the secure intake; if the company's
 * monitoring affiliate links (panel above) are set, they get the SmartCredit /
 * MyFreeScoreNow choice on the way. Contract: docs/INTAKE-LINK-SPEC.md.
 */
function SignupLinkCard({ link, onChange }: {
  link: NonNullable<CompanySettings["intake_link"]>;
  onChange: (next: NonNullable<CompanySettings["intake_link"]>) => void;
}) {
  const [copied, setCopied] = useState<string | null>(null);
  const embedUrl = `${link.url}?embed=1`;
  const iframe = `<iframe src="${embedUrl}" title="Sign up" style="width:100%;max-width:520px;height:640px;border:0;border-radius:12px"></iframe>`;
  const button = `<a href="${link.url}?src=website" target="_blank" rel="noopener">Get started</a>`;
  async function copy(label: string, text: string) {
    try { await navigator.clipboard.writeText(text); setCopied(label); window.setTimeout(() => setCopied(null), 1600); }
    catch { setCopied(null); }
  }
  const Row = ({ label, value }: { label: string; value: string }) => (
    <div className="intake-url-row" style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <input readOnly value={value} onFocus={(e) => e.currentTarget.select()} style={{ flex: 1, fontFamily: "monospace", fontSize: 12 }} />
      <button type="button" className="btn-secondary btn-sm" onClick={() => copy(label, value)}>
        {copied === label ? "Copied" : "Copy"}
      </button>
    </div>
  );
  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <div className="panel-header"><h2>Your sign-up link</h2></div>
      <p className="muted small" style={{ margin: "0 0 12px" }}>
        Text this link to a new prospect, put it on your website, or drop the embed into a funnel.
        Whoever fills it in is added automatically{link.default_record_type ? ` as a ${link.default_record_type}` : ""},
        gets your monitoring enrollment choice, and continues into the secure intake — no need to create them first.
      </p>
      <div className="stacked-form">
        <Field label="Link (text it, or put it behind a button)"><Row label="link" value={link.url} /></Field>
        <Field label="Embed in a website or funnel page"><Row label="iframe" value={iframe} /></Field>
        <Field label="Button HTML (for page builders)"><Row label="button" value={button} /></Field>
        <div className="form-row" style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "center" }}>
          <label className="small" style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="checkbox" checked={link.enabled} onChange={(e) => onChange({ ...link, enabled: e.target.checked })} />
            Link is active
          </label>
          {link.default_record_type && (
            <label className="small" style={{ display: "flex", gap: 8, alignItems: "center" }}>
              New sign-ups become
              <select value={link.default_record_type}
                onChange={(e) => onChange({ ...link, default_record_type: e.target.value as "lead" | "client" })}>
                <option value="lead">Leads (review them first)</option>
                <option value="client">Clients (straight into the roster)</option>
              </select>
            </label>
          )}
        </div>
        <p className="muted small" style={{ margin: 0 }}>
          Add <code>?src=facebook</code> (or any label) to the link to see where sign-ups come from; UTM tags are kept too.
          Turning the link off makes it show "not active" instead of the form. Save to apply.
        </p>
      </div>
    </div>
  );
}
